import { selectField } from './form/select'
import { data, popup } from './store'
import type { Facets } from './types/vacancies'
import { caretIcon, h } from './utils'
import { renderVacancy } from './vacancy'

interface RenderVacanciesOptions {
  tags?: (keyof Facets)[]
}

export function renderVacancies(
  container: HTMLElement,
  options: RenderVacanciesOptions = {
    tags: ['Locations', 'EmploymentTypes', 'WorkLocationPreferences'],
  },
) {
  const root = h('div', { class: 'vbzc-vacancies' }, { parent: container })
  const filter = h('div', { class: 'vbzc-vacancies-filter' }, { parent: root })
  const ul = h('ul', { class: 'vbzc-vacancies-list' }, { parent: root })

  // Department filter
  const { field: departmentField, select: departmentSelect } = selectField({
    label: ZC_DEPARTMENT_LABEL,
    name: 'department',
    choices: [
      { value: '', label: ZC_ALL_LABEL, selected: true },
      ...[
        ...new Set(
          data.vacancies
            .map((v) => v.Facets.Categories?.[0]?.Name)
            .filter(Boolean)
            .sort(),
        ),
      ].map((value) => ({ value, label: value })),
    ],
  })
  departmentSelect.addEventListener('change', renderList)
  filter.append(departmentField)

  // Location filter
  const { field: locationField, select: locationSelect } = selectField({
    label: ZC_LOCATION_LABEL,
    name: 'location',
    choices: [
      { value: '', label: ZC_ALL_LABEL, selected: true },
      ...[
        ...new Set(
          data.vacancies
            .map((v) => v.WorkLocation.City)
            .filter(Boolean)
            .sort(),
        ),
      ].map((value) => ({ value, label: value })),
    ],
  })
  locationSelect.addEventListener('change', renderList)
  filter.append(locationField)

  function renderList() {
    while (ul.firstChild) {
      ul.removeChild(ul.firstChild)
    }

    const filteredVacancies = data.vacancies.filter((vacancy) => {
      const department = departmentSelect.value
      const location = locationSelect.value
      return (
        (!department || vacancy.Facets.Categories?.[0]?.Name === department) &&
        (!location || vacancy.WorkLocation.City === location)
      )
    })

    // List
    for (const vacancy of filteredVacancies) {
      const li = h('li', { class: 'vbzc-vacancies-list-item' }, { parent: ul })
      const button = h('button', { class: 'vbzc-vacancies-list-item-button', type: 'button' }, { parent: li })

      // Title
      h(
        'span',
        { class: 'vbzc-vacancies-list-item-title' },
        {
          children: [
            h('span', { class: 'vbzc-h4' }, { text: vacancy.Title }),
            h('span', { class: 'vbzc-vacancies-list-item-caret' }, { children: [caretIcon('right')] }),
          ],
          parent: button,
        },
      )

      // Tags
      if (options.tags?.length) {
        const tags = h('span', { class: 'vbzc-tags' }, { parent: button })

        for (const tag of options.tags) {
          for (const { Name } of vacancy.Facets[tag] ?? []) {
            h('span', { class: 'vbzc-tag' }, { text: Name, parent: tags })
          }
        }
      }

      // Handle click
      button.addEventListener('click', async () => {
        await renderVacancy(vacancy, popup.clear().elements.container, options)
        popup.show()
        const queryString = new URLSearchParams(window.location.search)
        queryString.set('vacancy', vacancy.Id)
        window.history.pushState({}, '', `${window.location.pathname}?${queryString.toString()}`)
      })
    }

    // No results
    if (!filteredVacancies.length) {
      h('li', { class: 'vbzc-vacancies-list-item' }, { parent: ul, text: ZC_NO_VACANCIES_MESSAGE })
    }
  }

  renderList()
}
